import { useState, useEffect } from 'react';
import { 
  Container, 
  CssBaseline, 
  ThemeProvider, 
  createTheme,
  Box,
  Button,
  Typography,
  Stack,
  useMediaQuery
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { LeadFormData } from './types';
import { useWorkflow } from './hooks/useWorkflow';
import Header from './components/Header';
import LeadForm from './components/LeadForm';
import WorkflowProgress from './components/WorkflowProgress';
import ResultsDisplay from './components/ResultsDisplay';
import apiService from './services/apiService';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

function App() {
  const { state, startWorkflow, resetWorkflow, findBusinessContacts, reloadBusiness, cancelWorkflow, exportResultsToCSV, loadMoreResults, isAutoLoadingMore, noMoreResults } = useWorkflow();
  const [showForm, setShowForm] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Track API states for potential future UI enhancements
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentSearchApi, setCurrentSearchApi] = useState(apiService.getCurrentSearchApi());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentContactApi, setCurrentContactApi] = useState(apiService.getCurrentContactApi());
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Update the current search API when it changes
  useEffect(() => {
    const updateSearchApi = () => {
      setCurrentSearchApi(apiService.getCurrentSearchApi());
    };
    
    // Set up event listener for API changes
    window.addEventListener('searchApiChanged', updateSearchApi);
    
    // Clean up
    return () => {
      window.removeEventListener('searchApiChanged', updateSearchApi);
    };
  }, []);

  // Update the current contact API when it changes
  useEffect(() => {
    const updateContactApi = () => {
      setCurrentContactApi(apiService.getCurrentContactApi());
    };
    
    // Set up event listener for API changes
    window.addEventListener('contactApiChanged', updateContactApi);
    
    // Clean up
    return () => {
      window.removeEventListener('contactApiChanged', updateContactApi);
    };
  }, []);

  const handleSubmit = async (data: LeadFormData) => {
    setShowForm(false);
    try {
      await startWorkflow(data);
    } catch (error) {
      console.error('Workflow error:', error);
    }
  };

  const handleReset = () => {
    resetWorkflow();
    setShowForm(true);
  };

  const handleUpdateSearch = () => {
    setShowForm(true);
    // We don't reset the workflow state so the form can be populated with current data
  };

  const handleCancel = () => {
    // Show a message to the user
    console.log('Cancelling all active requests...');
    // Call the cancelWorkflow function
    cancelWorkflow();
  };

  const handleFindContacts = async (businessIndex: number) => {
    try {
      await findBusinessContacts(businessIndex);
    } catch (error) {
      console.error('Error finding contacts:', error);
    }
  };

  const handleReloadBusiness = async (businessIndex: number) => {
    try {
      await reloadBusiness(businessIndex);
    } catch (error) {
      console.error('Error reloading business:', error);
    }
  };

  const handleLoadMore = async () => {
    if (!loadMoreResults) return;
    
    setIsLoadingMore(true);
    try {
      return await loadMoreResults();
    } catch (error) {
      console.error('Error loading more results:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Format the search summary
  const getSearchSummary = () => {
    if (!state.formData) return 'No search performed';
    
    const { location, radius, instructions } = state.formData;
    const businessCount = state.results.length;
    
    let summary = `${businessCount} businesses`;
    if (location) summary += ` near ${location}`;
    if (radius) summary += ` within ${radius} miles`;
    if (instructions) summary += ` matching "${instructions}"`;
    
    return summary;
  };

  // Calculate stats for the progress component
  const calculateStats = () => {
    const businessCount = state.results.length;
    const targetBusinessCount = state.formData?.maxResults || 10;
    
    // Count total contacts
    const contactsCount = state.results.reduce((total, business) => {
      return total + (business.teamMembers?.length || 0);
    }, 0);
    
    // Count businesses with contacts
    const businessesWithContactsCount = state.results.filter(business => 
      business.teamMembers && business.teamMembers.length > 0
    ).length;
    
    // Count businesses with websites
    const businessesWithWebsitesCount = state.results.filter(business => 
      business.website && business.website !== '#'
    ).length;
    
    return {
      businessCount,
      targetBusinessCount,
      contactsCount,
      businessesWithContactsCount,
      businessesWithWebsitesCount
    };
  };

  const stats = calculateStats();

  // Determine if there are any active requests
  const hasActiveRequests = state.currentStepId !== null || 
    state.results.some(business => business.isLoadingContacts || business.isLoadingBusiness) ||
    isAutoLoadingMore || 
    isLoadingMore;

  // Determine if any actions should be disabled
  const isLoading = !!state.currentStepId || isLoadingMore || isAutoLoadingMore;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        {showForm ? (
          <LeadForm 
            onSubmit={handleSubmit} 
            isLoading={false}
            initialData={state.formData}
          />
        ) : (
          <>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2,
              mb: 3
            }}>
              {/* Search summary */}
              <Typography 
                variant={isMobile ? "body1" : "h6"} 
                sx={{ 
                  fontWeight: 500,
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%'
                }}
              >
                <SearchIcon color="primary" />
                {getSearchSummary()}
              </Typography>
              
              {/* Action buttons - on a new row and right-aligned */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%'
              }}>
                <Stack 
                  direction={isMobile ? "column" : "row"} 
                  spacing={2}
                  sx={{ width: isMobile ? '100%' : 'auto' }}
                >
                  {hasActiveRequests && (
                    <Button 
                      variant="outlined" 
                      color="error"
                      onClick={handleCancel}
                      startIcon={<CancelIcon />}
                      fullWidth={isMobile}
                    >
                      Cancel
                    </Button>
                  )}
                  
                  <Button 
                    variant="outlined" 
                    color="primary"
                    onClick={handleUpdateSearch}
                    startIcon={<RefreshIcon />}
                    disabled={isLoading}
                    fullWidth={isMobile}
                  >
                    Update Search
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    onClick={handleReset}
                    disabled={isLoading}
                    fullWidth={isMobile}
                  >
                    Start New Search
                  </Button>
                </Stack>
              </Box>
            </Box>
            
            <WorkflowProgress 
              businessCount={state.results.length}
              totalBusinessCount={(state.formData as LeadFormData | undefined)?.maxResults ?? 0}
              contactCount={stats.contactsCount}
              businessWithContactsCount={stats.businessesWithContactsCount}
              exportReady={state.isComplete && state.results.length > 0}
              isLoadingBusinesses={state.currentStepId === 'finding'}
              isLoadingContacts={state.currentStepId === 'analyzing'}
              onExport={exportResultsToCSV}
              results={state.results}
            />
            
            <ResultsDisplay 
              results={state.results} 
              isComplete={state.isComplete} 
              message={state.message}
              error={state.error}
              suggestions={state.suggestions}
              onFindContacts={handleFindContacts}
              onReloadBusiness={handleReloadBusiness}
              onLoadMore={handleLoadMore}
              isLoadingMore={isLoadingMore || isAutoLoadingMore}
              formData={state.formData}
              noMoreResults={noMoreResults}
            />
          </>
        )}
      </Container>
    </ThemeProvider>
  );
}

export default App;
